import React, { useMemo } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  useCompanyControllerGetCompanyAsAdminQuery,
  useVoiceListControllerVoiceListQuery,
  useLlmProviderListControllerAdminProviderListQuery,
  useTranscriberListControllerAdminTranscriberListQuery,
} from '@shared/services/apiService/apiService';
import { SelectVoicesInput } from '@widgets/CompanyVoices/SelectVoicesInput';
import { SelectLlmProvidersInput } from '@widgets/CompanyLlmProviders/SelectLlmProvidersInput';
import { SelectTranscribersInput } from '@widgets/CompanyTranscribers/SelectTranscribersInput';

type OptionLimitsFieldsProps = {
  companyId: string;
  enabled: boolean;
  selectedVoiceIds: string[];
  onVoiceIdsChange: (nextIds: string[]) => void;
  selectedLlmProviderIds: string[];
  onLlmProviderIdsChange: (nextIds: string[]) => void;
  selectedTranscriberIds: string[];
  onTranscriberIdsChange: (nextIds: string[]) => void;
  disabled?: boolean;
};

export const OptionLimitsFields: React.FC<OptionLimitsFieldsProps> = ({
  companyId,
  enabled,
  selectedVoiceIds,
  onVoiceIdsChange,
  selectedLlmProviderIds,
  onLlmProviderIdsChange,
  selectedTranscriberIds,
  onTranscriberIdsChange,
  disabled,
}) => {
  const { t } = useTranslation();

  const { data: catalogVoices, isLoading: isCatalogVoicesLoading } =
    useVoiceListControllerVoiceListQuery(undefined, {
      skip: !enabled,
      refetchOnMountOrArgChange: true,
    });

  const { data: catalogProviders, isLoading: isCatalogProvidersLoading } =
    useLlmProviderListControllerAdminProviderListQuery(undefined, {
      skip: !enabled,
      refetchOnMountOrArgChange: true,
    });

  const { data: catalogTranscribers, isLoading: isCatalogTranscribersLoading } =
    useTranscriberListControllerAdminTranscriberListQuery(undefined, {
      skip: !enabled,
      refetchOnMountOrArgChange: true,
    });

  const { data: company, isLoading: isCompanyLoading } = useCompanyControllerGetCompanyAsAdminQuery(
    { id: companyId },
    { skip: !enabled || !companyId, refetchOnMountOrArgChange: true },
  );

  const isLoading =
    isCatalogVoicesLoading ||
    isCatalogProvidersLoading ||
    isCatalogTranscribersLoading ||
    isCompanyLoading;

  const displayVoices = useMemo(() => {
    if (company === undefined) {
      return [];
    }
    const catalog = catalogVoices ?? [];
    const catalogIds = new Set(catalog.map((v) => v.id));
    const extras = (company.voices ?? []).filter((v) => !catalogIds.has(v.id));
    return [...catalog, ...extras];
  }, [catalogVoices, company]);

  const displayProviders = useMemo(() => {
    if (company === undefined) {
      return [];
    }
    const catalog = catalogProviders ?? [];
    const catalogIds = new Set(catalog.map((p) => p.id));
    const extras = (company.llmProviders ?? []).filter((p) => !catalogIds.has(p.id));
    return [...catalog, ...extras];
  }, [catalogProviders, company]);

  const displayTranscribers = useMemo(() => {
    if (company === undefined) {
      return [];
    }
    const catalog = catalogTranscribers ?? [];
    const catalogIds = new Set(catalog.map((transcriber) => transcriber.id));
    const extras = (company.transcribers ?? []).filter(
      (transcriber) => !catalogIds.has(transcriber.id),
    );
    return [...catalog, ...extras];
  }, [catalogTranscribers, company]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('superAdminMainPage.companies.companyLimits.optionLimitsDescription')}
      </Typography>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {t('superAdminMainPage.companies.company.voices')}
      </Typography>
      <SelectVoicesInput
        voices={displayVoices}
        selectedIds={selectedVoiceIds}
        onChange={onVoiceIdsChange}
        disabled={disabled}
        portalDropdown
      />
      <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
        {t('superAdminMainPage.companies.company.llmProviders')}
      </Typography>
      <SelectLlmProvidersInput
        providers={displayProviders}
        selectedIds={selectedLlmProviderIds}
        onChange={onLlmProviderIdsChange}
        disabled={disabled}
        portalDropdown
      />
      <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
        {t('superAdminMainPage.companies.company.transcribers')}
      </Typography>
      <SelectTranscribersInput
        transcribers={displayTranscribers}
        selectedIds={selectedTranscriberIds}
        onChange={onTranscriberIdsChange}
        disabled={disabled}
        portalDropdown
      />
    </Box>
  );
};
